<template>
    <TransitionRoot as="template" :show="open">
        <Dialog class="relative z-50" @close="close">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-50 w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                            <div class="mt-2 text-center">
                                <DialogTitle as="h3" class="text-base font-semibold text-gray-900">
                                    {{ $t('share_gallery_modal.title') }}
                                </DialogTitle>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500">
                                        {{ $t('share_gallery_modal.instructions') }}
                                    </p>
                                </div>

                                <div class="mt-3 grid grid-cols-1">
                                    <input
                                        type="text"
                                        readonly
                                        name="gallery-url"
                                        id="gallery-url"
                                        class="col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 sm:pr-9 sm:text-sm/6"
                                        :value="url"
                                    />
                                    <svg
                                        @click.prevent="copyUrl"
                                        class="cursor-pointer col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-gray-400 sm:size-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 18 18"
                                    >
                                        <g
                                            transform="translate(1 1)"
                                            stroke="#000"
                                            stroke-width="1.5"
                                            fill="none"
                                            fill-rule="evenodd"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        >
                                            <rect width="10.4" height="10.4" x="5.6" y="5.6" rx="1.6" />
                                            <path
                                                d="M2.4 10.4h-.8A1.6 1.6 0 010 8.8V1.6A1.6 1.6 0 011.6 0h7.2a1.6 1.6 0 011.6 1.6v.8"
                                            />
                                        </g>
                                    </svg>
                                </div>

                                <div class="mt-3">
                                    <p class="text-sm text-gray-500">
                                        {{ $t('share_gallery_modal.qr_instructions') }}
                                    </p>
                                </div>

                                <div class="mt-2 flex justify-center">
                                    <img
                                        class="size-50"
                                        :src="qrUrl"
                                        :alt="$t(name)"
                                    />
                                </div>
                            </div>

                            <div class="mt-5 sm:mt-6">
                                <a
                                    :href="url"
                                    target="_blank"
                                    class="inline-flex w-full justify-center rounded-md bg-royal-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-royal-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royal-blue-600"
                                >
                                    {{ $t('share_gallery_modal.view_button') }}
                                </a>
                                <button
                                    type="button"
                                    class="mt-2 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 cursor-pointer"
                                    @click="close"
                                >
                                    {{ $t('share_gallery_modal.close') }}
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { mapActions } from "vuex";

export default defineComponent({
    name: "ShareGalleryModalComponent",

    components: {
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
    },

    props: {
        open: {
            type: Boolean,
            required: true,
        },
        url: {
            type: String,
            required: true
        },
        qrUrl: {
            type: String,
            required: true
        },
        // хранит ключ перевода для alt
        name: {
            type: String,
            default: 'share_gallery_modal.qr_alt'
        }
    },

    methods: {
        ...mapActions({
            success: "alerts/success",
        }),

        close() {
            this.$emit('close');
        },

        copyUrl() {
            navigator.clipboard.writeText(this.url);
            this.success({
                message: this.$t('share_gallery_modal.copy_success')
            });
        }
    },
});
</script>
